import { usePinPlacement } from "../PassPlanner/usePinPlacement";
import {
  usePassSimulation,
  useSimulationTime,
} from "../PassPlanner/PassSimulationContext";
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { usePassPlanner } from "../PassPlanner/PassPlannerContext";
import { createObserverPin } from "../PassPlanner/createObserverPin";
import { Box } from "@mui/material";
import Globe, { GlobeMethods } from "react-globe.gl";
import * as THREE from "three";
import { Line2 } from "three/examples/jsm/lines/Line2.js";
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";
import { createSolarPanelTexture } from "./createSolarPanelTexture";
import SatPopover from "../SatPopover/SatPopover";
import { SatelliteTleData } from "../SatDataLoader/types";
import {
  useSatelliteCoverage,
  CoveragePointData,
} from "./hooks/useSatelliteCoverage";
import { useOrbitTrails, PathData } from "./hooks/useOrbitTrails";
import { getCurrentPositionFromTLE } from "../utils/getCurrentPositionFromTle";
import { toGlobeCoordinates } from "./utils/orbitGeometry";
import { updateOrbitLineGeometry } from "./utils/updateOrbitLineGeometry";

type GlobeItem = CoveragePointData | PathData;
const GLOBE_RADIUS = 100;
const COVERAGE_ALTITUDE = 0.001;
const SURFACE_NORMAL = new THREE.Vector3(0, 0, 1);

const createGlobeResources = () => {
  const texture = createSolarPanelTexture();
  const fill = new THREE.MeshBasicMaterial({
    color: 0x90ee90,
    transparent: true,
    opacity: 0.35,
    depthWrite: false,
    blending: THREE.NormalBlending,
    side: THREE.DoubleSide,
  });
  const trail = new LineMaterial({
    linewidth: 1.75,
    vertexColors: true,
    transparent: true,
    opacity: 0.65,
    depthWrite: false,
  });
  const marker = new THREE.PointsMaterial({
    map: texture,
    color: 0xffffff,
    size: 5,
    sizeAttenuation: true,
    alphaTest: 0.5,
    transparent: true,
    depthWrite: false,
    depthTest: true,
  });
  const coverageGeometry = new THREE.CircleGeometry(1, 64);
  const markerGeometry = new THREE.BufferGeometry();
  markerGeometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute([0, 0, 0], 3),
  );
  const pathGeometries = new Set<THREE.BufferGeometry>();
  return {
    texture,
    fill,
    trail,
    marker,
    coverageGeometry,
    markerGeometry,
    pathGeometries,
  };
};

type ThreeGlobeProps = {
  tleData: SatelliteTleData[];
  showSatTrails: boolean;
  focusTargetSatellite: SatelliteTleData | null;
  focusAnchorEl: HTMLElement | null;
  onFocusComplete: () => void;
};

export const ThreeGlobe: React.FC<ThreeGlobeProps> = ({
  tleData,
  showSatTrails,
  focusTargetSatellite,
  focusAnchorEl,
  onFocusComplete,
}) => {
  const { picking, location, chooseLocation } = usePassPlanner();
  const { session, fail } = usePassSimulation();
  const simulationTime = useSimulationTime();
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const observerLocation = session?.observer ?? location;
  const observerPins = useMemo(
    () => (observerLocation ? [observerLocation] : []),
    [observerLocation],
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const globeEl = useRef<GlobeMethods | undefined>();
  const { events: pinEvents, shouldIgnoreClick } = usePinPlacement({
    picking,
    placePin: (clientX, clientY) => {
      const globe = globeEl.current;
      if (!globe) return false;
      const bounds = globe.renderer().domElement.getBoundingClientRect();
      const coordinates = globe.toGlobeCoords(
        clientX - bounds.left,
        clientY - bounds.top,
      );
      if (!coordinates) return false;
      chooseLocation(coordinates);
      return true;
    },
  });
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [selectedSatellite, setSelectedSatellite] =
    useState<SatelliteTleData | null>(null);
  const { coveragePointData } = useSatelliteCoverage(tleData, simulationTime);
  const { orbitPathData } = useOrbitTrails(
    tleData,
    showSatTrails,
    simulationTime,
    session?.endTimeUTCms ?? null,
  );
  const resources = useMemo(createGlobeResources, []);
  const items = useMemo(
    () => [...coveragePointData, ...orbitPathData],
    [coveragePointData, orbitPathData],
  );
  const sizeRef = useRef(size);
  sizeRef.current = size;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(([entry]) => {
      const width = Math.round(entry.contentRect.width);
      const height = Math.round(entry.contentRect.height);
      setSize((previous) => {
        if (previous.width === width && previous.height === height)
          return previous;
        return { width, height };
      });
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  const previousSize = useRef(size);
  useEffect(() => {
    const previous = previousSize.current;
    previousSize.current = size;
    const globe = globeEl.current;
    if (
      !globe ||
      !previous.width ||
      !previous.height ||
      !size.width ||
      !size.height
    )
      return;
    const previousAspect = Math.min(1, previous.width / previous.height);
    const nextAspect = Math.min(1, size.width / size.height);
    if (Math.abs(previousAspect - nextAspect) < 0.001) return;
    const view = globe.pointOfView();
    const distance = ((view.altitude + 1) * previousAspect) / nextAspect;
    globe.pointOfView(
      { ...view, altitude: Math.min(5, Math.max(0.2, distance - 1)) },
      0,
    );
  }, [size]);

  useEffect(
    () => () => {
      resources.texture.dispose();
      resources.fill.dispose();
      resources.trail.dispose();
      resources.marker.dispose();
      resources.coverageGeometry.dispose();
      resources.markerGeometry.dispose();
      resources.pathGeometries.forEach((geometry) => geometry.dispose());
    },
    [resources],
  );

  const createObject = useCallback(
    (value: object) => {
      const data = value as GlobeItem;
      if (data.kind === "orbit") {
        const geometry = new LineGeometry();
        resources.pathGeometries.add(geometry);
        geometry.addEventListener("dispose", () =>
          resources.pathGeometries.delete(geometry),
        );
        const line = new Line2(geometry, resources.trail);
        line.renderOrder = 5;
        return line;
      }
      const group = new THREE.Group();
      const fill = new THREE.Mesh(resources.coverageGeometry, resources.fill);
      fill.name = "coverage";
      fill.renderOrder = 1;
      const marker = new THREE.Points(
        resources.markerGeometry,
        resources.marker,
      );
      marker.name = "satellite";
      // Trails never write depth; the glyph draws last while Earth still occludes it.
      marker.renderOrder = 30;
      group.add(fill, marker);
      return group;
    },
    [resources],
  );

  const updateObject = useCallback(
    (object: THREE.Object3D, value: object) => {
      const data = value as GlobeItem;
      if (data.kind === "orbit") {
        if (object.userData.points === data.points) return;
        const line = object as Line2;
        updateOrbitLineGeometry(line.geometry, data.points, GLOBE_RADIUS);
        resources.pathGeometries.add(line.geometry);
        object.userData.points = data.points;
        return;
      }
      const normal = new THREE.Vector3(
        ...toGlobeCoordinates([data.lat, data.lng, 0], 1),
      );
      object.quaternion.setFromUnitVectors(SURFACE_NORMAL, normal);
      const coverage = object.getObjectByName("coverage");
      if (coverage) {
        coverage.position.set(0, 0, GLOBE_RADIUS * (1 + COVERAGE_ALTITUDE));
        coverage.scale.setScalar(
          ((data.radius * Math.PI) / 180) * GLOBE_RADIUS,
        );
      }
      const marker = object.getObjectByName("satellite");
      if (marker) {
        marker.position.set(0, 0, GLOBE_RADIUS * (1 + data.alt));
      }
    },
    [resources],
  );

  const syncAnimationVisibility = useCallback(() => {
    const globe = globeEl.current;
    if (!globe) return;
    if (document.hidden) {
      globe.pauseAnimation();
      return;
    }
    globe.resumeAnimation();
  }, []);

  useEffect(() => {
    document.addEventListener("visibilitychange", syncAnimationVisibility);
    return () =>
      document.removeEventListener("visibilitychange", syncAnimationVisibility);
  }, [syncAnimationVisibility]);

  const focusSimulation = useCallback(() => {
    const snapshot = sessionRef.current;
    if (!snapshot || !globeEl.current) return;
    globeEl.current.pointOfView(
      { lat: snapshot.observer.lat, lng: snapshot.observer.lng, altitude: 1.8 },
      0,
    );
  }, []);

  useEffect(() => {
    if (!session) return;
    setPopoverAnchorEl(null);
    setSelectedSatellite(null);
    focusSimulation();
  }, [session, focusSimulation]);

  useEffect(() => {
    if (session && coveragePointData.length === 0) {
      fail(
        "Satellite position is unavailable at this time. Go back to passes and calculate a new pass.",
      );
    }
  }, [session, coveragePointData.length, fail]);

  const initializeGlobe = useCallback(() => {
    const globe = globeEl.current;
    if (!globe) return;
    const controls = globe.controls();
    controls.minDistance = GLOBE_RADIUS * 1.2;
    controls.maxDistance = GLOBE_RADIUS * 6;
    controls.enablePan = false;
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    globe.renderer().setPixelRatio(Math.min(window.devicePixelRatio, 2));
    const aspect = sizeRef.current.width / Math.max(1, sizeRef.current.height);
    globe.pointOfView(
      { lat: 20, lng: 0, altitude: Math.max(2.2, 2.2 / Math.min(1, aspect)) },
      0,
    );
    focusSimulation();
    syncAnimationVisibility();
  }, [syncAnimationVisibility, focusSimulation]);

  const catseMaterial = useMemo(() => {
    const texture = new THREE.TextureLoader().load("/catse.jpg");
    texture.colorSpace = THREE.SRGBColorSpace;
    return new THREE.MeshBasicMaterial({
      map: texture,
      side: THREE.DoubleSide,
    });
  }, []);
  const tileData = useMemo(
    () => [
      {
        name: "The King",
        lat: 59.33,
        lng: 18.06,
        altitude: 0.0002,
        width: 0.5,
        height: 0.25,
        material: catseMaterial,
      },
    ],
    [catseMaterial],
  );
  useEffect(
    () => () => {
      catseMaterial.map?.dispose();
      catseMaterial.dispose();
    },
    [catseMaterial],
  );

  useEffect(() => {
    if (session || !focusTargetSatellite || !globeEl.current) return;
    const position = getCurrentPositionFromTLE(focusTargetSatellite.tle);
    if (position) {
      const reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      globeEl.current.pointOfView(
        { lat: position.lat, lng: position.lng, altitude: 1.5 },
        reducedMotion ? 0 : 1000,
      );
      setSelectedSatellite(focusTargetSatellite);
      setPopoverAnchorEl(focusAnchorEl ?? containerRef.current);
    }
    onFocusComplete();
  }, [session, focusTargetSatellite, focusAnchorEl, onFocusComplete]);

  const handleClick = useCallback(
    (value: object, event: MouseEvent) => {
      if (shouldIgnoreClick(event) || sessionRef.current) return;
      const data = value as GlobeItem;
      if (data.kind !== "satellite") return;
      setSelectedSatellite(data.satellite);
      setPopoverAnchorEl(event.target as HTMLElement);
    },
    [shouldIgnoreClick],
  );

  useEffect(() => {
    if (!picking) return;
    setPopoverAnchorEl(null);
    setSelectedSatellite(null);
  }, [picking]);

  const handlePopoverClose = () => {
    setPopoverAnchorEl(null);
    setSelectedSatellite(null);
  };

  return (
    <Box
      ref={containerRef}
      {...pinEvents}
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        minWidth: 0,
        minHeight: 0,
        overflow: "hidden",
        cursor: picking ? "crosshair" : "grab",
        bgcolor: "#060c15",
        "& .scene-container canvas": {
          cursor: "inherit",
          "&:active": { cursor: picking ? "crosshair" : "grabbing" },
        },
        "& .float-tooltip-kap": { visibility: picking ? "hidden" : undefined },
      }}
    >
      {size.width > 0 && size.height > 0 && (
        <Globe
          ref={globeEl}
          animateIn={false}
          width={size.width}
          height={size.height}
          globeImageUrl="/textures/earth-blue-marble.jpg"
          bumpImageUrl="/textures/earth-topology.png"
          backgroundImageUrl="/textures/night-sky.png"
          backgroundColor="#060c15"
          onGlobeReady={initializeGlobe}
          htmlElementsData={observerPins}
          htmlLat="lat"
          htmlLng="lng"
          htmlAltitude={0.006}
          htmlElement={createObserverPin}
          customLayerData={items}
          customThreeObject={createObject}
          customThreeObjectUpdate={updateObject}
          pointerEventsFilter={(_object, data) => !picking || !data}
          customLayerLabel={picking ? () => "" : "name"}
          onCustomLayerClick={handleClick}
          tilesData={tileData}
          tileLabel={picking ? () => "" : "name"}
          tileLat="lat"
          tileLng="lng"
          tileAltitude="altitude"
          tileWidth="width"
          tileHeight="height"
          tileMaterial="material"
          tileCurvatureResolution={5}
          tilesTransitionDuration={0}
        />
      )}
      <SatPopover
        open={!session && !picking && Boolean(popoverAnchorEl)}
        anchorEl={popoverAnchorEl}
        onClose={handlePopoverClose}
        satellite={selectedSatellite}
      />
    </Box>
  );
};
